import { toast } from 'react-hot-toast';

export interface IntegrationData {
  source: 'discord' | 'wordpress';
  id: string;
  timestamp: number;
  // Discord-specific fields
  serverName?: string;
  // WordPress-specific fields
  site_url?: string;
  site_name?: string;
  integration_code?: string | null;
  return_url?: string;
}

/**
 * Store integration data in a cookie before redirecting to auth
 */
export const storeIntegrationData = (
  source: 'discord' | 'wordpress', 
  data: string | { serverId: string; serverName?: string } | { siteId: string; site_url?: string; site_name?: string },
  extraData?: { site_url?: string; site_name?: string; integration_code?: string | null; return_url?: string }
): void => {
  let integrationData: IntegrationData;
  
  if (typeof data === 'string') {
    // Legacy format - just ID, with optional extraData for WordPress
    integrationData = {
      source,
      id: data,
      timestamp: Date.now(),
      ...extraData
    };
  } else if ('serverId' in data) {
    // Discord format with server info
    integrationData = {
      source,
      id: data.serverId,
      serverName: data.serverName,
      timestamp: Date.now()
    };
  } else {
    // WordPress format with site info
    integrationData = {
      source,
      id: (data as any).siteId,
      site_url: (data as any).site_url,
      site_name: (data as any).site_name,
      timestamp: Date.now()
    };
  }
  
  const expires = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes
  document.cookie = `pendingIntegration=${encodeURIComponent(JSON.stringify(integrationData))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  
  console.log(`Stored ${source} integration data in cookie:`, integrationData);
};

/**
 * Retrieve and clear integration data from cookie
 */
export const getAndClearIntegrationData = (): IntegrationData | null => {
  const cookies = document.cookie.split(';').map(c => c.trim());
  const integrationCookie = cookies.find(c => c.startsWith('pendingIntegration='));
  
  if (!integrationCookie) {
    return null;
  }

  try {
    const cookieValue = decodeURIComponent(integrationCookie.split('=')[1]);
    const integrationData: IntegrationData = JSON.parse(cookieValue);
    
    // Check if cookie is not too old (within 10 minutes)
    const age = Date.now() - integrationData.timestamp;
    if (age >= 10 * 60 * 1000) {
      console.log('Integration cookie too old, ignoring');
      clearIntegrationData();
      return null;
    }
    
    // Clear the cookie immediately to prevent reprocessing
    clearIntegrationData();
    
    return integrationData;
  } catch (error) {
    console.error('Error parsing integration cookie:', error);
    clearIntegrationData();
    return null;
  }
};

/**
 * Clear integration data cookie
 */
export const clearIntegrationData = (): void => {
  document.cookie = 'pendingIntegration=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

/**
 * Generate integration URL for webhook messages
 */
export const generateIntegrationUrl = (baseUrl: string, source: 'discord' | 'wordpress', id: string): string => {
  return `${baseUrl}/dashboard?${source}_${source === 'discord' ? 'server' : 'site'}_id=${id}&source=${source}_webhook`;
};

/**
 * Show appropriate error messages for integration failures
 */
export const showIntegrationError = (source: 'discord' | 'wordpress', error: string): void => {
  const sourceLabel = source === 'discord' ? 'Discord' : 'WordPress';
  toast.error(`${sourceLabel} integration failed: ${error}`, { 
    id: `${source}-integration-error`,
    duration: 5000 
  });
};

/**
 * Show success messages for integration completion
 */
export const showIntegrationSuccess = (source: 'discord' | 'wordpress', message: string): void => {
  const sourceLabel = source === 'discord' ? 'Discord' : 'WordPress';
  toast.success(`${sourceLabel} integration successful: ${message}`, { 
    id: `${source}-integration-success`,
    duration: 4000 
  });
};